/**
 * StructuredInterestCards — renders the granular Known-Interests fields
 * produced by the Structured Intelligence Engine (Feb 2026 v2).
 *
 * Shows compact cards for:
 *   - Specific Indications        (therapeutic_areas_specific)
 *   - Excluded Modalities         (excluded_modalities)
 *   - Excluded Indications        (excluded_indications)
 *   - Scientific Preferences      (scientific_preferences)
 *
 * Each card is hidden when its array is empty. Read-only display —
 * edits happen through the InterestTagger flow.
 */
import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Target, Ban, FlaskConical, Sparkles, Pencil } from 'lucide-react';

const CARDS = [
  {
    key: 'therapeutic_areas_specific',
    label: 'Specific Indications',
    Icon: Target,
    iconClass: 'text-indigo-500',
    pillClass: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    testId: 'specific-indications-card',
  },
  {
    key: 'excluded_modalities',
    label: 'Excluded Modalities',
    Icon: Ban,
    iconClass: 'text-rose-500',
    pillClass: 'bg-rose-50 text-rose-700 border-rose-200',
    testId: 'excluded-modalities-card',
  },
  {
    key: 'excluded_indications',
    label: 'Excluded Indications',
    Icon: Ban,
    iconClass: 'text-rose-500',
    pillClass: 'bg-rose-50 text-rose-700 border-rose-200',
    testId: 'excluded-indications-card',
  },
  {
    key: 'scientific_preferences',
    label: 'Scientific Preferences',
    Icon: Sparkles,
    iconClass: 'text-teal-500',
    pillClass: 'bg-teal-50 text-teal-700 border-teal-200',
    testId: 'scientific-preferences-card',
  },
  {
    key: 'focus_notes',
    label: 'Focus Notes',
    Icon: Sparkles,
    iconClass: 'text-slate-500',
    pillClass: 'bg-slate-50 text-slate-700 border-slate-300',
    testId: 'focus-notes-card',
  },
];

// Aug 2026 · When the parent (PharmaContactDetailPage) passes `onEdit`, we
// render a small pencil button per card that opens the parent's edit
// modal so BD leads can add/remove chips without going through the
// AI Tagger flow. Falls back to read-only if `onEdit` is not provided
// (used elsewhere like the Company Profile page).
export const StructuredInterestCards = ({ entity, onEdit }) => {
  if (!entity) return null;
  // When onEdit is provided, always render all 4 cards (even empty) so
  // users can add chips from scratch. When read-only, hide empties.
  const visible = onEdit ? CARDS : CARDS.filter((c) => (entity[c.key] || []).length > 0);
  if (visible.length === 0) return null;
  return (
    <>
      {visible.map(({ key, label, Icon, iconClass, pillClass, testId }) => {
        const vals = entity[key] || [];
        return (
        <Card key={key} className="border border-slate-200 shadow-sm" data-testid={testId}>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Icon className={`w-4 h-4 ${iconClass}`} />
              <h3
                className="text-sm font-semibold text-slate-800 uppercase tracking-wider"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {label}
              </h3>
              <Badge variant="secondary" className="text-[10px]">
                {vals.length}
              </Badge>
              {onEdit && (
                <button
                  type="button"
                  onClick={() => onEdit(key)}
                  className="ml-auto p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-violet-600 transition-colors"
                  title={`Edit ${label}`}
                  aria-label={`Edit ${label}`}
                  data-testid={`${testId}-edit-btn`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {vals.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {vals.map((v, i) => (
                  <Badge
                    key={i}
                    className={`text-xs px-3 py-1 border ${pillClass}`}
                    data-testid={`${key}-chip-${i}`}
                  >
                    {v}
                  </Badge>
                ))}
              </div>
            ) : onEdit ? (
              <button
                type="button"
                onClick={() => onEdit(key)}
                className="text-xs text-slate-400 hover:text-violet-600 italic transition-colors"
                data-testid={`${testId}-add-first`}
              >
                No entries yet — click to add
              </button>
            ) : null}
          </CardContent>
        </Card>
        );
      })}
    </>
  );
};

export default StructuredInterestCards;
